<template>
	<div
		class="border bg-white text-xs sm:text-base text-black p-4 sm:p-8 mt-2 sm:mt-6 -ml-2 sm:ml-0 border-gray-300"
		:class="{
			'border-red-700':
				hasTriedToCheckout &&
				(missingFields.length > 0 || missingPatterns.length > 0),
			'border-gray-300':
				!hasTriedToCheckout ||
				(missingFields.length == 0 && missingPatterns == 0)
		}"
	>
		<div class="flex justify-between items-center">
			<div class="font-bold text-xl">
				{{ heading }}
			</div>
		</div>

		<div class="flex -ml-2 mt-6" v-if="!team.NameOptional">
			<participant-field
				label="Holdnavn"
				v-model="team.Name"
				:required="true"
				:show-valid="isValid('Name')"
				:show-required-message="isMissing('Name')"
				@blur="validate('Name')"
				@keyup="[saveParticipantData(), validate('Name', true)]"
				@change="[saveParticipantData(), validate('Name')]"
			/>
		</div>

		<div
			class="flex -ml-2 mt-6"
			v-for="field in team.ProductProperties"
			:key="field.Id"
		>
			<participant-field
				:label="field.Name"
				v-model="field.Value"
				:required="true"
				:pattern="field.Pattern !== '' ? field.Pattern : null"
				:pattern-message="field.PatternMessage"
				:show-valid="
					isValid(field.Id) &&
						(field.Pattern == null ||
							field.Pattern == '' ||
							isPatternValid(field.Id))
				"
				:show-required-message="isMissing(field.Id)"
				:show-pattern-message="
					!isMissing(field.Id) && isPatternMissing(field.Id)
				"
				@blur="[validateField(field), validatePattern(field)]"
				@keyup="
					[
						saveParticipantData(),
						validateField(field, true)
					]
				"
				@change="
					[
						saveParticipantData(),
						validateField(field)
					]
				"
			/>
		</div>

		<div
			class="flex -ml-2 mt-6"
			v-for="field in team.CustomFields"
			:key="field.Id"
		>
			<participant-field
				:label="field.Name"
				v-model="field.Value"
				:required="true"
				:pattern="field.Pattern !== '' ? field.Pattern : null"
				:pattern-message="field.PatternMessage"
				:show-valid="
					isValid(field.Id) &&
						(field.Pattern == null ||
							field.Pattern !== '' ||
							isPatternValid(field.Id))
				"
				:show-required-message="isMissing(field.Id)"
				:show-pattern-message="
					!isMissing(field.Id) && isPatternMissing(field.Id)
				"
				@blur="[validateField(field), validatePattern(field)]"
				@keyup="
					[
						saveParticipantData(),
						validateField(field, true)
					]
				"
				@change="
					[
						saveParticipantData(),
						validateField(field)
					]
				"
			/>
		</div>

		<div
			class="flex -ml-2 mt-6"
			v-for="field in team.DropdownProperties"
			:key="field.Id"
		>
			<participant-field
				type="select"
				:label="field.Name"
				v-model="field.Value"
				:values="field.Values"
				:required="true"
				:show-valid="isValid(field.Id)"
				:show-required-message="isMissing(field.Id)"
				@blur="validateField(field)"
				@change="
					[
						setFieldValue(field, $event),
						saveParticipantData(),
						validateField(field)
					]
				"
			/>
		</div>

		<div class="flex flex-wrap -ml-2 mt-6">
			<participant-field
				class="w-full md:w-1/2"
				type="email"
				label="E-mail"
				label-description="(anbefalet)"
				v-model="team.Email"
				:show-valid="isPatternValid('Email')"
				:show-pattern-message="isPatternMissing('Email')"
				pattern-error-message="Indtast venligst i formatet xx@xx.xx"
				@blur="
					[
						saveParticipantData(),
						validatePattern('Email', false, optionalEmailPattern)
					]
				"
				@keyup="
					[
						saveParticipantData()
					]
				"
				@change="
					[
						saveParticipantData()
					]
				"
			/>
			<participant-field
				class="w-full md:w-1/2"
				type="tel"
				label="Mobilnummer"
				label-description="(anbefalet)"
				v-model="team.Mobile"
				:show-valid="isPatternValid('Mobile')"
				pattern-error-message="Indtast venligst 8 cifre"
				:show-pattern-message="isPatternMissing('Mobile')"
				@blur="
					[
						saveParticipantData(),
						validatePattern('Mobile', false, optionalPhonePattern)
					]
				"
				@keyup="
					[
						saveParticipantData()
					]
				"
				@change="
					[
						saveParticipantData()
					]
				"
			/>
		</div>

		<div
			class="border border-gray-300 bg-white text-xs sm:text-base text-black p-4 -mx-4 sm:mx-0 sm:p-8 mt-6"
			v-if="editParticipants"
		>
			<div class="flex justify-between items-center">
				<div class="font-bold text-xl">
					Deltagere
					<small v-if="team.ParticipantMin > 0 && team.ParticipantMax > 0">
						({{ team.ParticipantMin }} - {{ team.ParticipantMax }} deltagere)
					</small>
					<small v-if="team.ParticipantMin > 0 && team.ParticipantMax == 0">
						(min. {{ team.ParticipantMin }}
						{{ team.ParticipantMin == 1 ? "deltager" : "deltagere" }})
					</small>
					<small v-if="team.ParticipantMax > 0 && team.ParticipantMin == 0">
						(max. {{ team.ParticipantMax }}
						{{ team.ParticipantMax == 1 ? "deltager" : "deltagere" }})
					</small>
				</div>
				<div>
					<button
						type="button"
						class="border-gray-400 border rounded-sm text-top-text bg-top text-base font-bold inline-flex items-center py-2 px-4"
						v-if="team.ParticipantMax == 0 || team.Participants.length &lt; team.ParticipantMax"
						@click="addParticipant"
					>
						Tilføj deltager
					</button>
				</div>
			</div>

			<div
				v-for="(participant, index) in team.Participants"
				:key="index"
				class="border-b border-gray-300 -mx-8 px-8 py-3"
			>
				<div class="flex flex-wrap -mx-2 sm:mx-0">
					<participant-field
						class="w-full md:w-1/2"
						label="Fornavn"
						v-model="participant.FirstName"
						:required="true"
						:show-valid="isValid(index + '.FirstName')"
						:show-required-message="isMissing(index + '.FirstName')"
						@blur="validateParticipant(index, 'FirstName')"
						@keyup="
							[
								saveParticipantData(),
								validateParticipant(index, 'FirstName', true)
							]
						"
						@change="
							[
								saveParticipantData(),
								validateParticipant(index, 'FirstName')
							]
						"
					/>
					<participant-field
						class="w-full mt-6 md:mt-0 md:w-1/2"
						label="Efternavn"
						v-model="participant.LastName"
						:required="true"
						:show-valid="isValid(index + '.LastName')"
						:show-required-message="isMissing(index + '.LastName')"
						@blur="validateParticipant(index, 'LastName')"
						@keyup="
							[
								saveParticipantData(),
								validateParticipant(index, 'LastName', true)
							]
						"
						@change="
							[
								saveParticipantData(),
								validateParticipant(index, 'LastName')
							]
						"
					/>
					<div class="pt-8" v-if="allowDelete">
						<button
							type="button"
							class="border-gray-400 border rounded-sm text-red-700 text-base inline-flex items-center py-2 px-4 bg-white"
							@click="removeParticipant(index)"
							@disabled="team.ParticipantMin &gt; 0 && team.Participants.length &lt;= team.ParticipantMin"
						>
							<span class="mr-2">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									class="w-3 h-3 fill-current"
									viewBox="0 0 12 12"
								>
									<path
										d="M6 7l-4 5-2-2 5-4-5-4 2-2 4 5 4-5 2 2-5 4 5 4-2 2-4-5z"
									/>
								</svg>
							</span>
							Slet
						</button>
					</div>
				</div>

				<div
					class="flex mt-4 -mx-2 sm:mx-0"
					v-for="field in participant.CustomFields"
					:key="field.Id"
				>
					<participant-field
						:label="field.Name"
						:name="field.Id"
						v-model="field.Value"
						:required="true"
						:pattern="field.Pattern !== '' ? field.Pattern : null"
						:pattern-message="field.PatternMessage"
						:pattern-error-message="field.PatternMessage"
						:show-valid="
							isValid(index + '.' + field.Id) &&
								(field.Pattern == null ||
									field.Pattern == '' ||
									isPatternValid(index + '.' + field.Id))
						"
						:show-required-message="isMissing(index + '.' + field.Id)"
						:show-pattern-message="
							!isMissing(index + '.' + field.Id) &&
								isPatternMissing(index + '.' + field.Id)
						"
						@blur="
							[
								validateParticipantField(index, field),
								validateParticipantPattern(index, field)
							]
						"
						@keyup="
							[
								saveParticipantData(),
								validateParticipantField(index, field, true)
							]
						"
						@change="
							[
								saveParticipantData(),
								validateParticipantField(index, field)
							]
						"
					/>
				</div>

				<div
					class="flex mt-4 -mx-2 sm:mx-0"
					v-for="field in participant.DropdownProperties"
					:key="field.Id"
				>
					<participant-field
						type="select"
						:label="field.Name"
						:name="field.Id"
						v-model="field.Value"
						:values="field.Values"
						:required="true"
						:show-valid="isValid(index + '.' + field.Id)"
						:show-required-message="isMissing(index + '.' + field.Id)"
						@blur="validateParticipantField(index, field)"
						@change="
							[
								setParticipantFieldValue(index, field, $event),
								saveParticipantData(),
								validateParticipantField(index, field)
							]
						"
					/>
				</div>

				<div
					class="flex mt-4 flex-wrap -mx-2 sm:mx-0"
					v-if="requireBirthDateAndGender"
				>
					<participant-field
						class="md:w-1/2"
						label="Køn"
						name="gender"
						type="select-string"
						v-model="participant.Gender"
						:values="[
							{ Key: 'Mand', Value: 'Mand' },
							{ Key: 'Kvinde', Value: 'Kvinde' },
							{ Key: 'Andet', Value: 'Andet' }
						]"
						:required="true"
						:show-valid="isValid(index + '.Gender')"
						:show-required-message="isMissing(index + '.Gender')"
						@blur="validateParticipant(index, 'Gender')"
						@change="
							[
								setParticipantValue(index, 'Gender', $event),
								saveParticipantData(),
								validateParticipant(index, 'Gender', true)
							]
						"
					/>

					<participant-field
						class="mt-6 md:mt-0 md:w-1/2"
						label="Fødselsdato"
						name="dateOfBirth"
						type="date"
						placeholder="dd/mm/åååå"
						v-model="participant.DateOfBirthString"
						pattern-error-message="Indtast venligst i formatet dd/mm/åååå"
						:required="true"
						:show-valid="
							isValid(index + '.DateOfBirthString') &&
								isPatternValid(index + '.DateOfBirthString')
						"
						:show-required-message="isMissing(index + '.DateOfBirthString')"
						:show-pattern-message="
							!isMissing(index + '.DateOfBirthString') &&
								isPatternMissing(index + '.DateOfBirthString')
						"
						@blur="
							[
								validateParticipant(index, 'DateOfBirthString'),
								validateParticipantPattern(
									index,
									'DateOfBirthString',
									false,
									dobPattern
								)
							]
						"
						@keyup="
							[
								saveParticipantData(),
								validateParticipant(index, 'DateOfBirth', true)
							]
						"
						@change="
							[
								saveParticipantData(),
								validateParticipant(index, 'DateOfBirthString')
							]
						"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import debounce from "lodash/debounce";
import ParticipantField from "./ParticipantField";

export default {
	props: {
		team: Object,
		editParticipants: Boolean,
		blankTeam: Object,
		index: Number
	},
	data: function() {
		return {
			validatedFields: [],
			validFields: [],
			validPatterns: [],
			missingFields: [],
			missingPatterns: [],
			zipPattern: /^\d{4}$/g,
			phonePattern: /^\d{8}$/g,
			optionalEmailPattern: /^(.{0}|[^@\s]+@[^@\s]+\.[^@\s]+)$/g,
			optionalPhonePattern: /^(.{0}|\d{8})$/g,
			emailPattern: /^[^@\s]+@[^@\s]+\.[^@\s]+$/g,
			dobPattern: /^(?:[012][0-9]|3[01])\/(?:0[0-9]|1[0-2])\/(?:19|20)[0-9]{2}$/g
		};
	},
	components: {
		ParticipantField
	},
	methods: {
		setValue: function(property, value) {
			this.team[property] = value;
		},
		setFieldValue: function(property, value) {
			property.Value = value;
		},
		setParticipantValue: function(index, property, value) {
			this.team.Participants[index][property] = value;
		},
		setParticipantFieldValue: function(index, property, value) {
			property.Value = value;
		},
		saveParticipantData: function() {
			this.$store.dispatch("saveBasket", this.$store.getters.basket);
		},
		isValid: function(fieldName) {
			return (
				this.validatedFields.indexOf(fieldName) > -1 &&
				this.validFields.indexOf(fieldName) > -1
			);
		},
		isPatternValid: function(fieldName) {
			return this.validPatterns.indexOf(fieldName) > -1;
		},
		isMissing: function(fieldName) {
			return this.missingFields.indexOf(fieldName) > -1;
		},
		isPatternMissing: function(fieldName) {
			return this.missingPatterns.indexOf(fieldName) > -1;
		},
		validate: function(property, ifInvalid) {
			if (!ifInvalid || this.isMissing(property)) {
				this.validatedFields.push(property);
				this.validFields = this.validFields.filter(f => f != property);
				this.missingFields = this.missingFields.filter(f => f != property);
				var valid = this.team[property] && this.team[property] != "";

				if (!valid) {
					this.missingFields.push(property);
				} else {
					this.validFields.push(property);
				}
				this.reportValidation();
			}
		},
		validateParticipant: function(index, property, ifInvalid) {
			if (!ifInvalid || this.isMissing(index + "." + property)) {
				this.validatedFields.push(index + "." + property);
				this.validFields = this.validFields.filter(
					f => f != index + "." + property
				);
				this.missingFields = this.missingFields.filter(
					f => f != index + "." + property
				);
				var valid =
					this.team.Participants[index] &&
					this.team.Participants[index][property] &&
					this.team.Participants[index][property] != "";

				if (!valid) {
					this.missingFields.push(index + "." + property);
				} else {
					this.validFields.push(index + "." + property);
				}
				this.reportValidation();
			}
		},
		validateField: function(property, ifInvalid) {
			if (!ifInvalid || this.isMissing(property.Id)) {
				this.validatedFields.push(property.Id);
				this.validFields = this.validFields.filter(f => f != property.Id);
				this.missingFields = this.missingFields.filter(f => f != property.Id);
				var valid = property.Value && property.Value != "";

				if (!valid) {
					this.missingFields.push(property.Id);
				} else {
					this.validFields.push(property.Id);
				}
				this.reportValidation();
			}
		},
		validateParticipantField: function(index, property, ifInvalid) {
			if (!ifInvalid || this.isMissing(index + "." + property.Id)) {
				this.validatedFields.push(index + "." + property.Id);
				this.validFields = this.validFields.filter(
					f => f != index + "." + property.Id
				);
				this.missingFields = this.missingFields.filter(
					f => f != index + "." + property.Id
				);
				var valid = property.Value && property.Value != "";

				if (!valid) {
					this.missingFields.push(index + "." + property.Id);
				} else {
					this.validFields.push(index + "." + property.Id);
				}
				this.reportValidation();
			}
		},
		validatePattern: function(property, ifInvalid, pattern) {
			var id = property.Id || property;
			if (!ifInvalid || this.isPatternMissing(id)) {
				pattern = pattern || property.Pattern;
				if (pattern && pattern !== "") {
					this.validatedFields.push(id);
					this.missingPatterns = this.missingPatterns.filter(f => f != id);
					this.validPatterns = this.validPatterns.filter(f => f != id);
					var value = property.Value
						? property.Value
						: this.team[property] ?? "";

					if (value) {
						var valid = value.match(pattern) != null;
						if (!valid) {
							this.missingPatterns.push(id);
						} else {
							this.validPatterns.push(id);
						}
					}
					this.reportValidation();
				}
			}
		},
		validateParticipantPattern: function(index, property, ifInvalid, pattern) {
			var id = property.Id || property;
			id = index + "." + id;
			if (!ifInvalid || this.isPatternMissing(id)) {
				pattern = pattern || property.Pattern;
				if (pattern && pattern !== "") {
					this.validatedFields.push(property.Id);
					this.validPatterns = this.validPatterns.filter(f => f != id);
					this.missingPatterns = this.missingPatterns.filter(f => f != id);
					var value = property.Value
						? property.Value
						: this.team.Participants[index][property] ?? "";
					var valid = value.match(pattern) != null;

					if (!valid) {
						this.missingPatterns.push(id);
					} else {
						this.validPatterns.push(id);
					}
					this.reportValidation();
				}
			}
		},
		addParticipant: function() {
			if (
				this.team.ParticipantMax == 0 ||
				this.team.Participants.length < this.team.ParticipantMax
			) {
				var newParticipant = JSON.parse(
					JSON.stringify(this.blankTeam.BlankParticipant)
				);
				this.team.Participants.push(newParticipant);
				this.saveParticipantData();
				this.validateAll();
			}
		},
		removeParticipant: function(index) {
			this.team.Participants.splice(index, 1);
			this.validateAll();
		},
		reportValidation: function() {
			var missing = this.missingFields.concat(this.missingPatterns);
			this.$store.dispatch("reportValidation", {
				olIndex: this.olIndex,
				index: this.index,
				missingFields: [...new Set(missing)].length
			});
		},
		validateAll: function() {
			if (this.hasTriedToCheckout) {
				this.missingFields = [];
				this.missingPatterns = [];

				if (!this.team.NameOptional) {
					this.validate("Name");
				}
				this.team.ProductProperties.map(field => {
					this.validateField(field);
					this.validatePattern(field);
				});
				this.team.CustomFields.map(field => {
					this.validateField(field);
					this.validatePattern(field);
				});
				this.team.DropdownProperties.map(field => {
					this.validateField(field);
				});

				this.validatePattern("Email", false, this.optionalEmailPattern);
				this.validatePattern("Mobile", false, this.optionalPhonePattern);

				if (this.editParticipants) {
					this.team.Participants.map((p, i) => {
						this.validateParticipant(i, "FirstName");
						this.validateParticipant(i, "LastName");

						if (this.requireBirthDateAndGender) {
							this.validateParticipant(i, "Gender");
							this.validateParticipant(i, "DateOfBirthString");
							this.validateParticipantPattern(
								i,
								"DateOfBirthString",
								false,
								this.dobPattern
							);
						}

						p.CustomFields.map(field => {
							this.validateParticipantField(i, field);
							this.validateParticipantPattern(i, field);
						});
						p.DropdownProperties.map(field => {
							this.validateParticipantField(i, field);
						});
					});
				}
			}
		}
	},
	computed: {
		hasTriedToCheckout: function() {
			return this.$store.getters.hasTriedToCheckout;
		},
		heading: function() {
			return "Hold " + (this.index + 1);
		},
		allowDelete: function() {
			return (
				this.team.ParticipantMin > 0 &&
				this.team.ParticipantMin < this.team.Participants.length
			);
		},
		requireBirthDateAndGender: function() {
			return (
				this.team.Participants.filter(
					p => p.CustomFields.filter(c => c.Pattern !== null && c.Pattern !== "").length > 0
				).length > 0
			);
		}
	},
	mounted: function() {
		this.validateAll();
	},
	watch: {
		hasTriedToCheckout: function() {
			this.validateAll();
		}
	}
};
</script>
